import gql from 'graphql-tag';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';

import LayeredCanvas from '@/core/layered-canvas';

import { ACFMediaFragment } from '@/fragments';
import { useViewSize } from '@/providers/ViewSizeContext';
import { useClientMount } from '@/providers/ClientMountProvider';

import { TypePill, HeroBorder, CursorFollower, RhLink, Image as RHImage } from '@components';
import { TypePillRefs } from '../TypePill/TypePill';

import { getLinkData, dedupeLineBreaks } from '@/core/utils';
import { useDebug } from '@/utils/useDebugLog';
import { getReduceMotion } from '@/utils/reduceMotion';
import { toProxiedWpUrl } from '@/utils/wpMediaUrl';

import { gsap } from 'gsap';
import SplitText from 'gsap/SplitText';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import useParallax from '@/components/Parallax/useParallax';

import HeroHours from './Hours';
import { hasAccentedCharacters } from './constants';
import { animateHeadlineIn } from './animateHeadlineIn';
import { useHeroCarousel } from './useHeroCarousel';
import { buildHeroSlides, type HeroProps, type SlideData } from './buildHeroSlides';

// SVG icons
import IconPause from '@/assets/svg/pause.svg';
import IconPlay from '@/assets/svg/arrow-right.svg';

// Styles
import styles from './Hero.module.scss';
import className from 'classnames/bind';
const cx = className.bind(styles);

// ----------------------------------------

function HeroContent({ builtSlides, ...props }: HeroProps & SlideData & { builtSlides: SlideData[] }) {
  const { log } = useDebug({ name: 'Hero', bgColor: 'seagreen', textColor: 'bisque', enabled: false });

  const {
    image,
    video,
    assetType,
    variant,
    headline,
    mobileHeadline,
    headlineSize,
    bodySize,
    eyebrow,
    logo,
    body,
    contentType,
    layeredAnimate,
    disableOverlay,
    showHoursAddress,
    customCursorText,
    linkHero,
    heroLink,
    videoThumbnail,
    showReverb,
  } = props;

  // ------------------------------ DEV FLAGS

  const DEV_FAKE_ONDATA = false; // Pretend media/canvas have loaded; set visible and skip canvas.animateLayers().
  const DEV_BYPASS_FONTS = false; // Treat fonts as "ready" so SplitText/timeline can initialize immediately.
  const DEV_DISABLE_REVERT = false; // Do not revert SplitText after anim completes (for debugging)
  const DEV_FORCED_TIMEOUT = 456; // ms to delay timeline start (for debugging)
  const DEV_DEBUG_DUPE = false; // Render a second non-animated headline for comparison

  const ENABLE_HOVER_PAUSE = false; // Set to FALSE to disable mouseenter/mouseleave pause behavior

  // ------------------------------ Booleans

  const { isDesktop, isReady } = useViewSize();
  const { mounted } = useClientMount();

  const isVideo = assetType?.[0] === 'video';
  const shouldBeSubtle = !isVideo;
  const shouldUseLayered = Boolean(layeredAnimate);

  // ------------------------------ Text processing

  const [activeHeadline, setActiveHeadline] = useState('');

  // Determine headline size classes based on CMS values, with defaults
  const headlineSizeClass = useMemo(() => {
    switch (headlineSize?.[0]) {
      case 'small':
        return 'display-xl';
      default:
        return 'display-xxl';
    }
  }, [headlineSize]);

  // Determine body size classes based on CMS values, with defaults
  const bodySizeClass = useMemo(() => {
    switch (bodySize?.[0]) {
      case 'large':
        return 'title-lc-s';
      default:
        return 'text-l';
    }
  }, [bodySize]);

  // Resolve the active headline for the current viewport:
  // desktop uses `headline`, mobile falls back to `mobileHeadline` if provided.
  useEffect(() => {
    if (!mounted) return;
    const headlineToUse = isDesktop ? headline : (mobileHeadline || headline);
    setActiveHeadline(dedupeLineBreaks(headlineToUse));
  }, [mounted, isDesktop]);

  // ------------------------------ Check for accented characters
  // If headline contains accented characters, we bypass SplitText animation

  const shouldBypassSplitText = useMemo(() => {
    return hasAccentedCharacters(activeHeadline);
  }, [activeHeadline]);

  // ------------------------------ Check for long words
  // If any word of the headline text is longer than 9 characters, we need to
  // apply a smaller font size on mobile to avoid overflow.

  const isSmallHeadlineRequired = useMemo(() => {
    const words = activeHeadline.split(/\s+/);
    return words.some(word => word.length > 9);;
  }, [activeHeadline]);

  // ------------------------------ State

  const [isVisible, setIsVisible] = useState(false); // Has media loaded and is visible?
  const [slide0VideoSrc, setSlide0VideoSrc] = useState<string | undefined>(undefined); // Slide 0 video src tracked in state so it survives re-renders
  const [isFontsReady, setIsFontsReady] = useState(false); // Wait for document fonts to be ready
  const [isAnimInStarted, setIsAnimInStarted] = useState(false); // Has anim in started?
  const [isAnimInComplete, setIsAnimInComplete] = useState(false); // Has anim in completed?
  const [showCursorFollower, setShowCursorFollower] = useState(false); // Show the cursor follower?

  // Carousel: which slide currently "owns" the CursorFollower.
  // activeSlideIndex flips immediately on transition start (used to hide the old slide's follower).
  // enteringSlideIndex flips after the exit duration (used to show the new slide's follower
  // alongside its content enter, giving us a clean re-init window for per-slide cursor text).
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [enteringSlideIndex, setEnteringSlideIndex] = useState(0);

  const isHeadlineOneLine = useRef(false);

  // Element refs
  const rootEl = useRef<HTMLDivElement>(null);
  const borderEl = useRef<HTMLDivElement>(null);
  const contentEl = useRef<HTMLDivElement>(null);
  const canvasContainerEl = useRef<HTMLDivElement>(null);
  const imageContainerEl = useRef<HTMLDivElement>(null);
  const canvasEl = useRef<HTMLDivElement>(null);
  const videoEl = useRef<HTMLVideoElement>(null);
  const imageEl = useRef<HTMLImageElement>(null);
  const headlineWrapEl = useRef<HTMLDivElement>(null);
  const headlineEl = useRef<HTMLHeadingElement>(null);
  const splitRef = useRef<SplitText | null>(null);
  const bodyEl = useRef<HTMLParagraphElement>(null);
  const logoEl = useRef<HTMLImageElement>(null);
  const hoursAddressEl = useRef<HTMLDivElement>(null);
  const pillRefs = useRef<TypePillRefs>({ rootEl: null, backgroundEl: null, textEl: null, iconEl: null });

  // Carousel refs
  const viewportRef = useRef<HTMLDivElement>(null);
  const dotsContainerRef = useRef<HTMLDivElement>(null);
  const timerCircleRef = useRef<SVGCircleElement | null>(null);
  const pauseButtonRef = useRef<HTMLButtonElement>(null);
  const prevArrowRef = useRef<HTMLButtonElement>(null);
  const nextArrowRef = useRef<HTMLButtonElement>(null);
  const slideContentRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Per-slide video refs so the carousel can pause / reset / restart them on slide changes
  const slideVideoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  // Per-slide poster overlay refs — faded out on canplay, reset on slide exit
  const slidePosterOverlayRefs = useRef<(HTMLImageElement | null)[]>([]);
  // Slide 0 overlay fade gates: both must be true before the overlay fades.
  // Prevents the overlay from fading while imageContainerEl is still at opacity 0
  // (canplay can fire before the container has finished fading in).
  const slide0CanPlayFired = useRef(false);
  const slide0ContainerReady = useRef(false);
  // Guard against double-triggering (e.g. a seek-to-0 re-fires canplay).
  const slide0OverlayFaded = useRef(false);
  const carouselControlsRef = useRef<HTMLDivElement>(null);

  // ------------------------------ Video
  //#region Video

  const canvas = useRef<LayeredCanvas | null>(null);

  // Determine if video should autoplay
  const [autoPlay, setAutoPlay] = useState<boolean>(false);
  useEffect(() => {
    setAutoPlay(!getReduceMotion());
  }, []);

  // Wait for font loading to complete before running SplitText (avoids
  // "SplitText called before fonts loaded" warning).
  useEffect(() => {
    // DEV: allow bypassing the Font Loading API gate
    if (DEV_BYPASS_FONTS) {
      setIsFontsReady(true);
      return;
    }

    if (!(document as any).fonts || !(document as any).fonts.ready) {
      // If the Font Loading API isn't available, assume fonts are ready.
      setIsFontsReady(true);
      return;
    }

    let cancelled = false;
    (document as any).fonts.ready.then(() => {
      if (!cancelled) setIsFontsReady(true);
    }).catch(() => {
      // If fonts.ready rejects for any reason, proceed to avoid blocking.
      if (!cancelled) setIsFontsReady(true);
    });

    return () => { cancelled = true; };
  }, [DEV_BYPASS_FONTS]);

  // Debug: log which media variant we're using (video vs image) and a short src
  useEffect(() => {
    if (isVideo) {
      log('Hero using VIDEO (not image)', { srcPreview: video?.slice(0, 120) });
    } else {
      log('Hero using IMAGE (not video)', { srcPreview: image?.node?.mediaItemUrl?.slice(0, 120) });
    }
  }, [isVideo, video, image]);

  // Fade out slide 0's poster overlay once both the video is ready (canplay) and the
  // imageContainerEl has finished fading in. Whichever condition is met last triggers the fade.
  // If autoplay is disabled (reduced motion, etc.), keep the overlay visible permanently so the
  // poster image shows as a static background rather than revealing a paused first frame.
  const tryFadeSlide0Overlay = () => {
    if (!autoPlay) return;
    if (slide0OverlayFaded.current) return;
    if (!slide0CanPlayFired.current || !slide0ContainerReady.current) return;
    slide0OverlayFaded.current = true;
    const overlay = slidePosterOverlayRefs.current[0];
    if (!overlay) return;
    // Seek to frame 0 while the overlay still covers the video so the reveal
    // always starts from the beginning, regardless of how long autoplay has been running.
    if (videoEl.current) try { videoEl.current.currentTime = 0; } catch {}
    gsap.to(overlay, { opacity: 0, duration: 0.8, ease: 'power2.inOut' });
  };

  // Callback when video/image data has loaded
  const onDataLoaded = () => {
    log('onDataLoaded called', { isVideo, isVisible, imageEl: !!imageEl.current, videoEl: !!videoEl.current });

    // ~ ~ ~ ~ ~ ~ ~ ~ TEMP: short-circuit when faking
    if (DEV_FAKE_ONDATA) {
      log('DEV_FAKE_ONDATA active: forcing visible and skipping canvas.animateLayers()');
      setIsVisible(true);
      return;
    }
    // ~ ~ ~ ~ ~ ~ ~ ~ end TEMP

    if (isVisible) {
      log('Media is already visible - skipping animation');
      return;
    }
    setIsVisible(true);
    if (!canvas.current) {
      log('No canvas to animate');
      return;
    }
    canvas.current.animateLayers();
  }

  // Load video poster
  const loadVideoPoster = () => {
    if (!videoEl.current) {
      log('loadVideoPoster: No video element');
      return;
    }
    log('LOAD VIDEO POSTER');
    // Commit src to state immediately so React owns it and won't wipe it on any
    // re-render that occurs while the poster image is still loading.
    setSlide0VideoSrc(video as string);
    const img = new Image();
    img.src = toProxiedWpUrl(videoThumbnail?.node?.mediaItemUrl) as string;
    img.onload = () => {
      log('video poster loaded');
      onDataLoaded();
    };
    videoEl.current.src = video as string;
  };

  // Load video poster if it's a video and the video element is available
  useEffect(() => {
    if (isVideo && videoEl.current) loadVideoPoster();
  }, [isVideo, videoEl]);

  // Initialize the LayeredCanvas instance when the component mounts
  useEffect(() => {
    // Only initialize layered canvas for videos when layeredAnimate was requested
    if (!shouldUseLayered) {
      log('Skipping layered canvas init because shouldUseLayered is false');
      return;
    }
    if (!canvasEl.current || canvas.current) {
      log('Skipping layered canvas init because canvas is already initialized');
      return;
    }

    const [type] = assetType || [];
    const isVideoType = type === 'video';
    const assetUrl = isVideoType ? (video || '') : (toProxiedWpUrl(image?.node?.mediaItemUrl) || '');
    const poster = isVideoType ? (toProxiedWpUrl(videoThumbnail?.node?.mediaItemUrl) || undefined) : undefined;

    log('LayeredCanvas - CREATE', { type, isVideoType, assetUrl });
    canvas.current = new LayeredCanvas({
      container: canvasEl.current,
      onDataLoaded,
      isVideo: isVideoType,
      assetUrl,
      poster,
      reduceMotion: getReduceMotion(),
      shouldBeSubtle, // New subtle animation flag
    });

  }, [shouldUseLayered, assetType, video, image?.node?.mediaItemUrl, videoThumbnail?.node?.mediaItemUrl]);

  // If we're faking, ensure visibility without waiting for anything.
  useEffect(() => {
    if (DEV_FAKE_ONDATA) {
      // Make sure we don't repeatedly spam state when already visible.
      if (!isVisible) {
        log('DEV_FAKE_ONDATA: forcing visible on mount');
        setIsVisible(true);
      }
    }
  }, [DEV_FAKE_ONDATA]);

  //#endregion Video
  // ------------------------------ Link
  //#region Link

  const heroLinkData = linkHero && heroLink?.linkType ? getLinkData(heroLink, true) : null;

  const anchorRef = useRef<HTMLAnchorElement>(null);
  // Per-slide anchor refs so each slide's CursorFollower can target its own link container.
  const slideAnchorRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const renderCursorFollower = heroLinkData;

  //#endregion Link
  // ------------------------------ Parallax
  //#region Parallax

  useParallax({ containerRef: rootEl, start: 'top top', distancePx: 120, isEnabled: true });

  //#endregion Parallax
  // ------------------------------ Animate in
  //#region Animate In

  const timelineAnimIn = useRef<GSAPTimeline | null>(null);
  const timelineCreated = useRef(false);

  // Position for CursorFollower during anim in
  const [externalCursorPos, setExternalCursorPos] = useState<{x: number, y: number} | null>(null);

  // Calculate cursor follower position based on border element
  const calculateCursorPosition = useCallback(() => {
    if (!borderEl.current) return null;

    const rect = borderEl.current.getBoundingClientRect();
    let posX, posY;

    if (isDesktop) {
      // Desktop positioning: near bottom-right
      const offset = Math.min(rect.width * 0.25, 300);
      posX = rect.left + rect.width - offset;
      posY = rect.top + rect.height - offset;
    } else {
      // Mobile positioning: near bottom-right
      posX = rect.width - 96 + 32;
      posY = rect.height - 96 + 16;
    }

    return { x: posX, y: posY };
  }, [isDesktop]);

  // Timeline: Create sub-timeline for headline animation
  const createHeadlineTimeline = (gDur: number) => {

    // - - - - - - - - - - Create new sub-timeline

    const headlineTl = gsap.timeline({
      onComplete: () => {
        if (DEV_DISABLE_REVERT) return; // HERO-BUGS-WIP
        // Revert split text to original state
        splitRef.current?.revert();

        // MUST align text to center after split has reverted
        if (headlineWrapEl.current) {
          headlineWrapEl.current.style.textAlign = 'center';
        }
      }
    }).add('start');

    // - - - - - - - - - - Revert any existing split text

    if (splitRef.current) {
      splitRef.current.revert();
      splitRef.current = null;
    }

    // - - - - - - - - - - Delegate to shared helper
    // Page-load uses 'chars' fallback for single-line headlines.

    if (!headlineEl.current || !headlineWrapEl.current) return headlineTl;

    const { split, isSingleLine } = animateHeadlineIn({
      parentTl: headlineTl,
      position: 'start',
      wrapEl: headlineWrapEl.current,
      headlineEl: headlineEl.current,
      gDur,
      singleLineMode: 'chars',
    });

    splitRef.current = split;
    if (isSingleLine) isHeadlineOneLine.current = true;

    return headlineTl;

  };

  // Timeline: Create main anim in timeline
  const createAnimTimeline = () => {
    log('createAnimTimeline function called');

    // -------------------- Early returns

    // Don't initialize the timeline / SplitText until the fonts are loaded.
    // SplitText warns if run before fonts are ready.
    if (!isFontsReady && !DEV_BYPASS_FONTS) {
      log('Fonts not ready - Delaying createAnimTimeline');
      return;
    }

    // If timeline animation has already started, do not re-initialize.
    if (isAnimInStarted && timelineAnimIn.current) {
      log('Anim in already started - skipping timeline init');
      return;
    }

    // -------------------- Create timeline

    // If timeline already exists, kill to recreate
    if (timelineAnimIn.current) timelineAnimIn.current.kill();

    log('Timeline - CREATE');

    // Create timeline!
    const heroTl = gsap.timeline({ paused: true,
      onStart: () => {
        log('🎬 timeline onStart');
        rootEl.current?.style.removeProperty('opacity'); // Initially hidden with opacity 0 during site load
      },
      onComplete: () => {
        log('✅ timeline onComplete - setting isAnimInComplete to TRUE');
        setIsAnimInComplete(true);
      }
    });

    const gDur = 1; // General duration for animations

    // -------------------- Video / Image

    // Video
    if (isVideo) {
      // If canplay already fired before the animation started, the video was ready
      // immediately — suppress the overlay so the video fades in directly with no
      // poster transition. Only show the overlay on slow connections where the video
      // isn't ready yet when the hero first becomes visible.
      // Skip this when autoplay is disabled: keep the overlay as a permanent static poster.
      if (autoPlay && slide0CanPlayFired.current) {
        const overlay = slidePosterOverlayRefs.current[0];
        if (overlay) gsap.set(overlay, { opacity: 0 });
        if (videoEl.current) try { videoEl.current.currentTime = 0; } catch {}
        slide0OverlayFaded.current = true;
      }

      const fadeTargets: HTMLElement[] = [];
      if (canvasContainerEl.current) fadeTargets.push(canvasContainerEl.current);
      if (imageContainerEl.current) fadeTargets.push(imageContainerEl.current);

      heroTl.fromTo(fadeTargets, {
        opacity: 0
      }, {
        opacity: 1,
        duration: gDur,
        ease: 'power4.inOut'
      });
      // Once the container is fully visible, check if canplay already fired.
      // If so, fade the overlay now. If not, the onCanPlay handler will do it.
      heroTl.call(() => {
        slide0ContainerReady.current = true;
        tryFadeSlide0Overlay();
      });
    }

    // Image
    else {
      if (imageEl.current) {
        heroTl.fromTo(
          imageEl.current,
          { opacity: 0 },
          { opacity: 1, duration: gDur, ease: 'power4.inOut' }
        );
      }
    }

    // ------------------------------ Border

    if (borderEl.current) {
      heroTl.add('border-in', '1');
      const borderStartScale = shouldBeSubtle ? 0.8 : 0.5;
      heroTl.fromTo(borderEl.current, {
        scale: borderStartScale,
        opacity: 0
      }, {
        scale: 1,
        opacity: 1,
        duration: gDur,
        ease: 'power4.inOut'
      }, 'border-in');
    }

    // ------------------------------ Headline

    if (headlineEl.current) {

      // Add a label on the main timeline
      heroTl.add('headline-in', '-=0.95');

      // Add the newly created sub-timeline to the main timeline
      heroTl.add(createHeadlineTimeline(gDur), 'headline-in');

    }

    // ------------------------------ Pill

    if (pillRefs.current?.rootEl) {
      heroTl.add('pill-in', '-=0.4');

      const pillTl = gsap.timeline().add('start');

      // Pill background
      pillTl.fromTo(pillRefs.current.backgroundEl, {
        scaleX: 0
      }, {
        scaleX: 1,
        duration: 0.6,
        ease: 'expo.out'
      }, 'start');

      // Pill text
      pillTl.fromTo(pillRefs.current.textEl, {
        y: '150%'
      }, {
        y: 0,
        duration: 0.6,
        ease: 'expo.out'
      }, 'start+=0.3');

      // Pill icon (if present)
      if (pillRefs.current.iconEl) {
        pillTl.set(pillRefs.current.iconEl, { zIndex: 1 }, 'start');
        pillTl.fromTo(pillRefs.current.iconEl, {
          y: '150%'
        }, {
          y: 0,
          duration: 0.6,
          ease: 'expo.out'
        }, 'start+=0.3');
      }

      heroTl.add(pillTl, 'pill-in');
    }

    // ------------------------------ Body text

    if (bodyEl.current) {
      heroTl.add('body-in', '-=0.4');
      heroTl.fromTo(bodyEl.current, {
        rotate: 1,
        y: '1rem',
        opacity: 0
      }, {
        rotate: 0,
        y: 0,
        opacity: 1,
        duration: gDur * 0.33,
        ease: 'power1.out',
        transformOrigin: '25% center',
      }, 'body-in');
    }

    if (logoEl.current) {
      heroTl.add('logo-in', '-=0.4');
      heroTl.fromTo(logoEl.current, {
        opacity: 0,
        y: '1.5rem',
        scaleY: 0.9
      }, {
        opacity: 1,
        y: 0,
        scaleY: 1,
        transformOrigin: '50% 100%',
        ease: 'power3.out',
        duration: gDur / 1.75
      }, 'body-in');
    }

    // ------------------------------ Hours + Address

    if (hoursAddressEl.current) {
      heroTl.add('hours-address-in', '-=0.4');
      heroTl.fromTo(hoursAddressEl.current, {
        opacity: 0,
        y: '1.5rem',
        scaleY: 0.9
      }, {
        opacity: 1,
        y: 0,
        scaleY: 1,
        transformOrigin: '50% 100%',
        ease: 'power3.out',
        duration: gDur / 1.75
      }, 'hours-address-in');
    }

    // ------------------------------ Carousel controls

    if (isCarousel && carouselControlsRef.current) {
      gsap.set(carouselControlsRef.current, { opacity: 0, y: '1rem' });
      heroTl.add('controls-in', '-=0.2');
      heroTl.fromTo(carouselControlsRef.current, {
        opacity: 0,
        y: '1rem'
      }, {
        opacity: 1,
        y: 0,
        duration: 0.4,
        ease: 'power2.out'
      }, 'controls-in');
    }

    // ------------------------------ CursorFollower

    if (heroLinkData && borderEl.current) {
      heroTl.add('cursor-in', '-=0.3');
      heroTl.call(() => {
        log('CURSOR IN');

        // Calculate and set CursorFollower position
        const pos = calculateCursorPosition();
        if (pos) {
          setExternalCursorPos(pos);
        }

        // Show the CursorFollower
        setShowCursorFollower(true);
      }, null, 'cursor-in');
    }

    // ------------------------------
    // Finalize timeline setup

    timelineAnimIn.current = heroTl;

    // ------------------------------
    // Start the timeline

    timelineAnimIn.current!.play();
    setIsAnimInStarted(true);

  };

  // Timeline: Initialize inside GSAP context (only once)
  useGSAP(() => {
    gsap.registerPlugin(ScrollTrigger, SplitText);
  }, []);

  // Create timeline when conditions are ready (only once)
  useEffect(() => {
    log('Timeline creation effect triggered', { isFontsReady, isVisible, timelineCreated: timelineCreated.current });

    if (!isFontsReady || !isVisible || timelineCreated.current) return;

    timelineCreated.current = true;
    setTimeout(createAnimTimeline, DEV_FORCED_TIMEOUT);
  }, [isFontsReady, isVisible, activeHeadline]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timelineAnimIn.current) {
        timelineAnimIn.current.kill();
        timelineAnimIn.current = null;
      }
      if (splitRef.current) {
        log('Clean up SplitText');
        splitRef.current.revert();
        splitRef.current = null;
      }
    };
  }, []);

  // If image is already loaded (from cache), set isVisible to true
  useEffect(() => {
    if (imageEl.current && imageEl.current.complete && imageEl.current.naturalHeight !== 0) {
      log('Image already loaded from cache');
      setIsVisible(true);
    }
  }, []);

  //#endregion Animate In
  // ------------------------------ Carousel
  //#region Carousel

  const slides = builtSlides;
  const isCarousel = slides.length > 1;

  // Carousel controller — owns slide DOM discovery, autoplay timer, hover-pause,
  // pause/play button, swipe/drag, and per-slide transition animations.
  useHeroCarousel({
    enabled: isCarousel,
    isAnimInComplete,
    enableHoverPause: ENABLE_HOVER_PAUSE,
    viewportRef,
    dotsContainerRef,
    timerCircleRef,
    pauseButtonRef,
    prevButtonRef: prevArrowRef,
    nextButtonRef: nextArrowRef,
    slideContentRefs,
    rootElRef: rootEl,
    log,
    // Hide outgoing slide's CursorFollower immediately when transition begins.
    // Also pre-seek the entering slide's video to frame 0 while it is still invisible.
    // This gives the browser the full slide-exit duration (~550ms) to decode the first
    // frame before the slide becomes visible, eliminating the poster-flash on entry.
    onSlideExit: (_prev, next) => {
      setActiveSlideIndex(next);
      const vNext = slideVideoRefs.current[next];
      if (vNext) { try { vNext.currentTime = 0; } catch {} }
    },
    // Reveal incoming slide's CursorFollower when its content begins entering.
    // Also restarts the entering slide's video directly (not via a useEffect) to avoid the
    // React state-bailout problem where setEnteringSlideIndex(0) is a no-op when already at 0.
    onSlideEnter: (next) => {
      setEnteringSlideIndex(next);

      // If slide 0 uses a layered canvas instead of a video, reset it here and bail out.
      // slideVideoRefs.current[0] would be null for canvas slides, so the video path below
      // would exit early and the canvas animation would never be reset.
      if (next === 0 && canvas.current) {
        canvas.current.animateLayers();
        return;
      }

      const v = slideVideoRefs.current[next];
      if (!v) return;
      // Brief delay so the browser finishes the current paint before we play.
      // Without this, play() can be silently rejected during rapid manual navigation.
      // (currentTime is already reset to 0 in onSlideExit, while the slide is invisible.)
      setTimeout(() => {
        const p = v.play();
        if (p && typeof p.then === 'function') {
          p.then(() => {
            if (next === 0) return;
            const overlay = slidePosterOverlayRefs.current[next];
            if (overlay) gsap.to(overlay, { opacity: 0, duration: 0.5, ease: 'power2.inOut' });
          }).catch(() => {});
        }
      }, 50);
    },
    // Once the full transition is settled, pause and reset the outgoing slide's video,
    // and restore the poster overlay so the next re-entry starts from a visible poster.
    onTransitionComplete: (prev, next) => {
      const v = slideVideoRefs.current[prev];
      if (v) { v.pause(); try { v.currentTime = 0; } catch {} }
      const overlay = slidePosterOverlayRefs.current[prev];
      if (overlay) gsap.set(overlay, { opacity: 1 });
      if (prev === 0) slide0OverlayFaded.current = false;
    },
  });

  //#endregion Carousel

  // Measure each slide's natural content height and write --slide-content-min-h onto the
  // section so that min-height always accommodates the tallest slide, even on short viewports.
  // Re-runs only on browser resize (not on slide changes).
  useEffect(() => {
    if (!isCarousel || !isFontsReady || !rootEl.current) return;

    const measure = () => {
      if (!rootEl.current) return;
      let maxNeeded = 0;

      for (const contentEl of slideContentRefs.current) {
        if (!contentEl) continue;

        // The .content element is a flex child inside a row-flex .container with
        // align-items: stretch. Setting height: auto alone is not enough — the
        // flex stretch still sizes the item to the container's (constrained) height.
        // align-self: flex-start breaks the stretch so offsetHeight reflects the
        // element's true natural content height.
        const prevHeight = contentEl.style.height;
        const prevAlignSelf = contentEl.style.alignSelf;
        contentEl.style.height = 'auto';
        contentEl.style.alignSelf = 'flex-start';

        // Walk offset parents up to the section to get the content's top offset.
        let offsetFromSection = 0;
        let el: HTMLElement | null = contentEl;
        while (el && el !== rootEl.current) {
          offsetFromSection += el.offsetTop;
          el = el.offsetParent as HTMLElement | null;
        }

        const needed = offsetFromSection + contentEl.offsetHeight;

        contentEl.style.height = prevHeight;
        contentEl.style.alignSelf = prevAlignSelf;

        if (needed > maxNeeded) maxNeeded = needed;
      }

      rootEl.current.style.setProperty('--slide-content-min-h', `${maxNeeded}px`);
    };

    // Re-measure on every browser resize using rAF to batch rapid resize events.
    let rafId: number | null = null;
    const onResize = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };

    // Measure immediately, then schedule a deferred second pass via double-rAF.
    // Safari doesn't always finish its initial layout (svh + aspect-ratio) before
    // the effect fires, so the first measure can read stale dimensions. The
    // double-rAF guarantees we're past the first full paint cycle.
    measure();
      let deferredRafId: number | null = requestAnimationFrame(() => {
        deferredRafId = requestAnimationFrame(() => {
          measure();
          window.dispatchEvent(new Event("resize"));
        });
      });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (deferredRafId !== null) cancelAnimationFrame(deferredRafId);
    };
  }, [isCarousel, isFontsReady]);

  // ------------------------------ Render

  return (
    <>
    <section className={cx([
        'hero',
        variant[0],
        { 'has-carousel': isCarousel }
      ])}
      ref={rootEl}
      style={{ opacity: 0 }}
    >

      {/* ---------- Carousel viewport (contains all slides) */}

      <div className={cx('carousel-viewport')} ref={viewportRef}>
        <div className={cx('carousel-container')}>

          {slides.map((slide, slideIndex) => {
            const isFirstSlide = slideIndex === 0;
            const slideIsVideo = slide.assetType?.[0] === 'video';
            const slideShouldUseLayered = Boolean(slide.layeredAnimate);
            const slideBodyCopy = dedupeLineBreaks(slide.body || '');
            const slideHeadlineSizeClass = slide.headlineSize?.[0] === 'small' ? 'display-xl' : 'display-xxl';
            const slideBodySizeClass = slide.bodySize?.[0] === 'large' ? 'title-lc-s' : 'text-l';
            const slideLinkData = slide.linkHero && slide.heroLink?.linkType
              ? getLinkData(slide.heroLink, true)
              : null;

            return (
              <div className={cx('carousel-slide')} key={slideIndex}>

                {/* Media background */}
                <div className={cx(['media-bg', { 'media-bg--no-overlay': slide.disableOverlay }])}>
                  <div className={`${cx('media-bg__inner')} js-parallax`}>
                    { slideShouldUseLayered ? (
                        <div className={cx('wrap_canvas')}
                          ref={isFirstSlide ? (el => {
                            canvasEl.current = el;
                            canvasContainerEl.current = el;
                          }) : undefined}
                        />
                      ) : (
                        <div className={cx('wrap_image-video')} ref={isFirstSlide ? imageContainerEl : undefined}>
                          { slideIsVideo ? (
                            <>
                            <video className={cx('video')}
                              autoPlay={isFirstSlide ? autoPlay : false}
                              playsInline
                              loop
                              muted
                              onLoadedData={isFirstSlide ? onDataLoaded : undefined}
                              onCanPlay={() => {
                                if (isFirstSlide) {
                                  onDataLoaded();
                                  slide0CanPlayFired.current = true;
                                  tryFadeSlide0Overlay();
                                } else {
                                  const overlay = slidePosterOverlayRefs.current[slideIndex];
                                  if (overlay) gsap.to(overlay, { opacity: 0, duration: 0.5, ease: 'power2.inOut' });
                                }
                              }}
                              // Slide 0: src initially set imperatively by loadVideoPoster, then committed to state
                              // so React owns it and won't wipe it during re-renders.
                              // Slides 1+: set src directly so the browser can fetch and decode the video.
                              src={!isFirstSlide ? (slide.video as string) : slide0VideoSrc}
                              ref={(el: HTMLVideoElement | null) => {
                                slideVideoRefs.current[slideIndex] = el;
                                if (isFirstSlide) videoEl.current = el;
                              }}
                            />
                            <RHImage
                              image={slide.videoThumbnail}
                              className={cx('poster-overlay')}
                              aria-hidden="true"
                              alt=""
                              ref={el => { slidePosterOverlayRefs.current[slideIndex] = el as HTMLImageElement | null; }}
                            />
                            </>
                          ) : (
                            <RHImage
                              image={slide.image}
                              onLoad={isFirstSlide ? () => { setIsVisible(true); } : undefined}
                              ref={isFirstSlide ? imageEl : undefined}
                            />
                          )}
                        </div>
                      )
                    }
                  </div>
                </div>

                {/* Content */}
                <div className={cx('container')}>
                  <div
                    className={cx('content')}
                    ref={el => {
                      slideContentRefs.current[slideIndex] = el;
                      if (isFirstSlide) contentEl.current = el;
                    }}
                  >

                    {/* Pill */}
                    { slide.eyebrow &&
                      <div className={cx('pill-wrap')}>
                        <TypePill
                          className={cx('hero-pill')}
                          type={(slide.contentType || contentType)?.[0]}
                          copy={slide.eyebrow}
                          ref={isFirstSlide ? pillRefs : undefined}
                        />
                      </div>
                    }

                    {/* Headline */}
                    <div
                      className={cx('headline-wrap')}
                      ref={isFirstSlide ? headlineWrapEl : undefined}
                      style={isFirstSlide && (isHeadlineOneLine.current || shouldBypassSplitText) ? { textAlign: 'center' } : undefined}
                    >
                      <h1
                        className={cx([
                          isFirstSlide ? headlineSizeClass : slideHeadlineSizeClass,
                          'headline',
                          { 'has-reverb': slide.showReverb },
                          { 'mobile-sm': isFirstSlide && isSmallHeadlineRequired },
                        ])}
                        ref={isFirstSlide ? headlineEl : undefined}
                        // Resolve per-slide headline: slide 0 uses `activeHeadline` state (animation-driven).
                        // Slides 1+ swap to `mobileHeadline` after mount when on mobile, else fall back to `headline`.
                        dangerouslySetInnerHTML={{
                          __html: isFirstSlide
                            ? (activeHeadline || '')
                            : dedupeLineBreaks(
                                (mounted && !isDesktop && slide.mobileHeadline)
                                  ? slide.mobileHeadline
                                  : (slide.headline || '')
                              ),
                        }}
                      />
                    </div>

                    {/* ~ ~ ~ ~ ~ ~ ~ DEBUG DUPLICATE (first slide only) */}
                    { isFirstSlide && DEV_DEBUG_DUPE && (
                      <div className={cx('headline-wrap')} style={{ textAlign: 'center' }}>
                        <h1
                          className={cx([
                            headlineSizeClass,
                            'headline',
                            { 'has-reverb': showReverb },
                            { 'mobile-sm': isSmallHeadlineRequired }
                          ])}
                          dangerouslySetInnerHTML={{ __html: activeHeadline || '' }}
                        />
                      </div>
                    )}
                    {/* ~ ~ ~ ~ ~ ~ ~ end DEBUG DUPLICATE */}

                    {/* Body */}
                    { slideBodyCopy && (
                      <div className={cx('body-wrap')}>
                        <p
                          className={cx('body', isFirstSlide ? bodySizeClass : slideBodySizeClass)}
                          ref={isFirstSlide ? bodyEl : undefined}
                          dangerouslySetInnerHTML={{ __html: slideBodyCopy }}
                        />
                      </div>
                    )}

                    {/* Logo */}
                    { slide.logo && (
                      <div className={cx('logo-wrap')}>
                        <RHImage image={slide.logo} ref={isFirstSlide ? logoEl : undefined} />
                      </div>
                    )}

                  </div>
                </div>

                {/* Per-slide link that covers the slide */}
                { !!slideLinkData && (
                  <RhLink
                    href={slideLinkData.url}
                    target={slideLinkData.target}
                    aria-label={slide.customCursorText}
                    className={cx('link')}
                    ref={(el: HTMLAnchorElement | null) => {
                      slideAnchorRefs.current[slideIndex] = el;
                      if (isFirstSlide) anchorRef.current = el;
                    }}
                  >
                    <div className={cx('focus-outline')} />
                    { mounted && isReady && isDesktop && (
                      <CursorFollower
                        container={{ get current() { return slideAnchorRefs.current[slideIndex]; } } as React.RefObject<HTMLElement>}
                        circleText={slide.customCursorText || customCursorText}
                        // External position (initial intro placement) only applies to slide 0
                        externalPos={isFirstSlide ? externalCursorPos : null}
                        // Visible only after intro completes AND this slide is both the active
                        // and entering slide (the gap between exit and enter is the re-init window)
                        isVisible={showCursorFollower && slideIndex === activeSlideIndex && slideIndex === enteringSlideIndex}
                        // Mouse events attach only when the follower is fully active for this slide
                        mouseActive={isAnimInComplete && slideIndex === activeSlideIndex && slideIndex === enteringSlideIndex}
                      />
                    )}
                  </RhLink>
                )}

              </div>
            );
          })}

        </div>
      </div>

      {/* ---------- Shared border (absolute, spans full hero) */}

      <div className={cx('border-wrapper')}>
        <div className={cx('border')} ref={borderEl}>
          <HeroBorder/>
        </div>
      </div>

      {/* Hours and address */}
      { showHoursAddress && (
        <HeroHours ref={hoursAddressEl} />
      )}

    </section>

    {/* ---------- Carousel controls (only when multi-slide) — block row below the section */}

    { isCarousel && (
      <div className={cx('carousel-controls')} ref={carouselControlsRef}>

        {/* Left group: Pause/play button + dot indicators */}
        <div className={cx('carousel-controls-left')}>

          {/* Pause / Play button with animated timer ring */}
          <button
            ref={pauseButtonRef}
            className={cx('carousel-play-toggle-button')}
            aria-label="Pause carousel"
          >
            <div className={cx('icon-wrapper')}>
              {/* Pause icon (hidden via CSS when button has data-paused="true") */}
              <IconPause className={cx('icon-pause')} />

              {/* Play icon (shown via CSS when button has data-paused="true") */}
              <IconPlay className={cx('icon-play')} />
            </div>

            {/* Timer ring — base track */}
            <svg className={cx('carousel-timer-ring-base')} viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="2" />
            </svg>

            {/* Timer ring — animated progress */}
            <svg className={cx('carousel-timer-ring')} viewBox="0 0 24 24" aria-hidden="true">
              <circle
                ref={timerCircleRef}
                cx="12" cy="12" r="11"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="69.12"
                strokeDashoffset="69.12"
              />
            </svg>
          </button>

          {/* Dot indicators */}
          <div ref={dotsContainerRef} className={cx('carousel-dots')} />

        </div>

        {/* Right group: Prev / Next arrow buttons */}
        <div className={cx('carousel-nav-arrows')}>
          <button
            ref={prevArrowRef}
            className={cx('carousel-arrow-button', 'carousel-arrow-button--prev')}
            aria-label="Previous slide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" aria-hidden="true">
              <path d="M20,.75h0c10.63,0,19.25,8.62,19.25,19.25h0c0,10.63-8.62,19.25-19.25,19.25h0C9.37,39.25.75,30.63.75,20h0C.75,9.37,9.37.75,20,.75Z" fill="transparent" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M18,25v-10l5,5-5,5Z" fill="currentColor" />
            </svg>
          </button>
          <button
            ref={nextArrowRef}
            className={cx('carousel-arrow-button', 'carousel-arrow-button--next')}
            aria-label="Next slide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" aria-hidden="true">
              <path d="M20,.75h0c10.63,0,19.25,8.62,19.25,19.25h0c0,10.63-8.62,19.25-19.25,19.25h0C9.37,39.25.75,30.63.75,20h0C.75,9.37,9.37.75,20,.75Z" fill="transparent" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M18,25v-10l5,5-5,5Z" fill="currentColor" />
            </svg>
          </button>
        </div>

      </div>
    )}

    </>
  );

};

export default function PrimaryHero(props: HeroProps) {
  const builtSlides = buildHeroSlides(props);
  if (!builtSlides.length) return null;
  // Slide-0 logic (media loading, layered canvas, headline animation) reads the flat props, so feed it the first built slide.
  return <HeroContent {...props} {...builtSlides[0]} builtSlides={builtSlides} />;
}

PrimaryHero.fragments = {
  entry: gql`
    ${ACFMediaFragment}
    fragment Hero on FlexiblePageContentContentHeroLayout {
      variant
      showHoursAddress
      layeredAnimate
      slides {
        assetType
        image {
          ...ACFMedia
        }
        video
        videoThumbnail {
          ...ACFMedia
        }
        disableOverlay
        headline
        mobileHeadline
        headlineSize
        bodySize
        eyebrow
        body
        logo {
          ...ACFMedia
        }
        contentType
        showReverb
        linkHero
        heroLink {
          linkType
          externalLink
          sectionId
          page {
            node {
              uri
            }
          }
        }
        customCursorText
      }
    }
  `,
};
